/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module TrafficCloneProfileModule
 */

import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';

import { TrafficCloneProfileCollection }
    from 'ajs/modules/traffic-clone-profile/factories/traffic-clone-profile.collection.factory';

import {
    IAviCollectionDataGridConfig,
    IAviCollectionDataGridRow,
} from 'ng/modules/data-grid';

import * as globalL10n from 'global-l10n';
import * as l10n from './traffic-clone-profile-page.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TTrafficCloneProfileCollection = typeof TrafficCloneProfileCollection;

/**
 * @description Traffic Clone Profile list page.
 *
 * @author Harmeet Kaur
 */
@Component({
    selector: 'traffic-clone-profile-page',
    templateUrl: './traffic-clone-profile-page.component.html',
})
export class TrafficCloneProfilePageComponent implements OnInit, OnDestroy {
    /**
     * Keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Get keys from global source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Collection object to store the traffic clone profile collection.
     */
    public trafficCloneProfileCollection: TrafficCloneProfileCollection;

    /**
     * Configuration object to display traffic clone profile collection.
     */
    public trafficClonePageGridConfig: IAviCollectionDataGridConfig;

    constructor(
        private readonly l10nService: L10nService,
        @Inject(TrafficCloneProfileCollection)
        public readonly TrafficCloneProfileCollection: TTrafficCloneProfileCollection,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.trafficCloneProfileCollection = new TrafficCloneProfileCollection();
    }

    /** @override */
    public ngOnInit(): void {
        const { objectName } = this.trafficCloneProfileCollection;

        this.trafficClonePageGridConfig = {
            id: `${objectName}-list-page`,
            collection: this.trafficCloneProfileCollection,
            fields: [
                {
                    id: 'name',
                    label: this.l10nService.getMessage(globalL10nKeys.nameLabel),
                    transform: (row: IAviCollectionDataGridRow) => row.getName(),
                },
                {
                    id: 'cloud',
                    label: this.l10nService.getMessage(globalL10nKeys.cloudLabel),
                    transform: (row: IAviCollectionDataGridRow) =>
                        row.getConfig().cloud_ref?.name(),
                },
            ],
            layout: {
                placeholderMessage: this.l10nService.getMessage(l10nKeys.placeholderMessage),
            },
        };
    }

    /** @override */
    public ngOnDestroy(): void {
        this.trafficCloneProfileCollection.destroy();
    }
}
